// pages/LinuxLoginAudit.jsx — Linux Login Audit across all EC2 hosts
// Backend: GET /api/linux-login-audit?days=N[&refresh=1] (admin only, linuxLoginAudit.js)
// Hosts = Remote Console SSH targets + the dashboard host itself.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../context/AuthContext';
import SecurityReviewPanel from './SecurityReviewPanel';
import { usePageAIContext } from '../context/AIPageContext';

const DAY_OPTIONS = [1, 3, 7, 14, 30, 90];
const PAGE = 200;

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function Kpi({ label, value, tone, sub }) {
  const tones = {
    neutral: 'bg-white border-gray-200 text-gray-800',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone] || tones.neutral}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      {sub && <div className="mt-0.5 text-xs opacity-70">{sub}</div>}
    </div>
  );
}

function Badge({ children, tone }) {
  const tones = {
    green: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    gray: 'bg-gray-100 text-gray-600',
    blue: 'bg-sky-100 text-sky-700',
  };
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ${tones[tone] || tones.gray}`}>{children}</span>;
}

const SECTIONS = ['summary', 'hosts', 'ssh', 'nginx'];
const LS_KEY = 'linuxLoginAudit.sections';
function loadOpen() {
  try { const v = JSON.parse(localStorage.getItem(LS_KEY) || 'null'); if (v && typeof v === 'object') return v; } catch (_) {}
  return {};
}
function Section({ id, title, summary, open, onToggle, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button type="button" onClick={() => onToggle(id)}
        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 ${open ? 'border-b border-gray-100' : 'rounded-xl'}`}>
        <span className={`inline-block text-xs text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        <span className="font-semibold text-gray-800">{title}</span>
        {summary && <span className="truncate text-xs text-gray-500">{summary}</span>}
        <span className="ml-auto text-xs text-gray-400">{open ? 'Collapse' : 'Expand'}</span>
      </button>
      {open && <div className="p-3">{children}</div>}
    </div>
  );
}

function NginxPanel({ hosts }) {
  const ngx = hosts.filter((h) => h.ok && h.nginx);
  const [sel, setSel] = useState('');
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => { setLimit(PAGE); }, [sel, kind, q]);
  if (!ngx.length) return null;
  const cur = ngx.find((h) => h.key === sel) || ngx[0];
  const n = cur.nginx;
  const ql = q.trim().toLowerCase();
  const ev = n.events.filter((e) =>
    (kind === 'all' || (kind === 'errors' && e.status >= 400) || (kind === 'login' && e.login) ||
      (kind === 'loginfail' && e.login && e.status >= 400) || (kind === 'probe' && e.probe) || (kind === 'external' && e.external)) &&
    (!ql || `${e.ip} ${e.path} ${e.ua} ${e.status}`.toLowerCase().includes(ql)));
  const stTone = (st) => (st >= 500 ? 'red' : st >= 400 ? 'amber' : st >= 300 ? 'gray' : 'green');
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3">
        <div className="mr-2 font-semibold text-gray-800">🌐 Nginx access</div>
        {ngx.map((h) => (
          <button key={h.key} onClick={() => setSel(h.key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${cur.key === h.key ? 'text-white' : 'bg-gray-100 text-gray-600'}`}
            style={cur.key === h.key ? { backgroundColor: 'rgb(var(--brand-600))' } : undefined}>
            {h.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-400">source: {n.source}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 p-3 md:grid-cols-4 xl:grid-cols-8">
        <Kpi label="Requests" value={n.total} />
        <Kpi label="4xx" value={n.s4xx} tone={n.s4xx ? 'amber' : 'neutral'} />
        <Kpi label="5xx" value={n.s5xx} tone={n.s5xx ? 'red' : 'neutral'} />
        <Kpi label="Client IPs" value={n.ips} />
        <Kpi label="External IPs" value={n.externalIps} tone={n.externalIps ? 'amber' : 'neutral'} />
        <Kpi label="Login OK" value={n.loginOk} tone="green" sub="POST to login paths" />
        <Kpi label="Login failed" value={n.loginFail} tone={n.loginFail ? 'red' : 'neutral'} />
        <Kpi label="Scanner probes" value={n.probes} tone={n.probesServed ? 'red' : n.probes ? 'amber' : 'neutral'}
          sub={n.probesServed ? `${n.probesServed} answered with real content!` : '.env, wp-login… none served'} />
      </div>
      <div className="overflow-x-auto px-3 pb-3">
        <div className="mb-1 text-xs font-semibold uppercase text-gray-500">Top client IPs</div>
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr><th className="px-3 py-2">IP</th><th className="px-3 py-2 text-right">Requests</th><th className="px-3 py-2 text-right">4xx</th>
              <th className="px-3 py-2 text-right">5xx</th><th className="px-3 py-2 text-right">Login fail</th><th className="px-3 py-2 text-right">Probes</th>
              <th className="px-3 py-2">Last seen</th><th className="px-3 py-2">User agent</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {n.topIps.slice(0, 15).map((r) => (
              <tr key={r.ip} className="cursor-pointer hover:bg-gray-50" onClick={() => setQ(r.ip)}>
                <td className="px-3 py-1.5 font-mono">{r.ip}{r.external && <span className="ml-1"><Badge tone="amber">external</Badge></span>}</td>
                <td className="px-3 py-1.5 text-right">{r.total}</td>
                <td className="px-3 py-1.5 text-right">{r.s4xx}</td>
                <td className={`px-3 py-1.5 text-right ${r.s5xx ? 'text-red-600' : ''}`}>{r.s5xx}</td>
                <td className={`px-3 py-1.5 text-right ${r.loginFail ? 'font-semibold text-red-600' : ''}`}>{r.loginFail}</td>
                <td className={`px-3 py-1.5 text-right ${r.probes ? 'font-semibold text-red-600' : ''}`}>{r.probes}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{fmt(r.last)}</td>
                <td className="max-w-xs truncate px-3 py-1.5 text-xs text-gray-500" title={r.ua}>{r.ua}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 p-3">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
          <option value="all">All notable requests</option><option value="errors">Errors (4xx/5xx)</option>
          <option value="login">Login requests</option><option value="loginfail">Failed logins</option>
          <option value="probe">Scanner probes</option><option value="external">External IP</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search IP / path / user agent…"
          className="min-w-[200px] flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        <span className="text-xs text-gray-400">{ev.length} shown{n.eventsTotal > n.events.length ? ` (latest ${n.events.length} of ${n.eventsTotal} notable)` : ''}</span>
      </div>
      <div className="max-h-[65vh] overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr><th className="px-3 py-2">Time</th><th className="px-3 py-2">Client IP</th><th className="px-3 py-2">Request</th>
              <th className="px-3 py-2">Status</th><th className="px-3 py-2">User agent</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ev.slice(0, limit).map((e, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 whitespace-nowrap">{fmt(e.time)}</td>
                <td className="px-3 py-1.5 font-mono">{e.ip}</td>
                <td className="max-w-md truncate px-3 py-1.5 font-mono text-xs" title={e.path}>
                  {e.method} {e.path}
                  {e.login && <span className="ml-1"><Badge tone="blue">login</Badge></span>}
                  {e.probe && <span className="ml-1"><Badge tone="red">probe</Badge></span>}
                  {e.probe && e.status === 200 && (e.spaFallback
                    ? <span className="ml-1" title="Answered with the dashboard's index.html (SPA catch-all), not the requested file"><Badge tone="gray">SPA fallback</Badge></span>
                    : <span className="ml-1" title="Probe answered 200 with a body that is NOT the SPA page — check what was served"><Badge tone="red">served — check!</Badge></span>)}
                </td>
                <td className="px-3 py-1.5"><Badge tone={stTone(e.status)}>{e.status}</Badge></td>
                <td className="max-w-xs truncate px-3 py-1.5 text-xs text-gray-500" title={e.ua}>{e.ua}</td>
              </tr>
            ))}
            {!ev.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No requests for this filter.</td></tr>}
          </tbody>
        </table>
      </div>
      {ev.length > limit && (
        <div className="border-t border-gray-100 p-3 text-center">
          <button onClick={() => setLimit(limit + PAGE)} className="text-sm font-medium" style={{ color: 'rgb(var(--brand-700))' }}>Show more</button>
        </div>
      )}
    </div>
  );
}

function LoginAuditView() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('events');
  const [hostFilter, setHostFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [externalOnly, setExternalOnly] = useState(false);
  const [showDash, setShowDash] = useState(false);
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [openMap, setOpenMap] = useState(loadOpen);
  const isOpen = (id) => openMap[id] !== false; // default expanded
  const saveOpen = (m) => { setOpenMap(m); try { localStorage.setItem(LS_KEY, JSON.stringify(m)); } catch (_) {} };
  const toggle = (id) => saveOpen(Object.assign({}, openMap, { [id]: !isOpen(id) }));
  const setAll = (v) => { const m = {}; SECTIONS.forEach((k) => { m[k] = v; }); saveOpen(m); };

  const load = useCallback(async (refresh) => {
    setLoading(true); setError('');
    try {
      const r = await api.get(`/api/linux-login-audit?days=${days}${refresh ? '&refresh=1' : ''}`);
      setData(r.data);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || e.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => { setLimit(PAGE); }, [hostFilter, resultFilter, externalOnly, showDash, q, tab, days]);

  const hosts = useMemo(() => (data && data.hosts) || [], [data]);

  const totals = useMemo(() => {
    const t = { ok: 0, down: 0, success: 0, failed: 0, ext: 0, root: 0, pwd: 0, active: 0 };
    hosts.forEach((h) => {
      if (!h.ok) { t.down += 1; return; }
      t.ok += 1; t.dash = (t.dash || 0) + (h.summary.fromDashboard || 0);
      t.success += h.summary.success; t.failed += h.summary.failed;
      t.ext += h.summary.externalSuccess + h.summary.externalFailed;
      t.root += h.summary.rootLogins; t.pwd += h.summary.passwordLogins; t.active += h.summary.activeSessions;
    });
    return t;
  }, [hosts]);

  const events = useMemo(() => {
    const rows = [];
    hosts.forEach((h) => {
      if (!h.ok) return;
      (h.sshd || []).forEach((e) => rows.push(Object.assign({ host: h.label, hostKey: h.key }, e)));
    });
    rows.sort((a, b) => (a.time < b.time ? 1 : -1));
    return rows;
  }, [hosts]);

  const sessions = useMemo(() => {
    const rows = [];
    hosts.forEach((h) => {
      if (!h.ok) return;
      (h.sessions || []).forEach((s) => rows.push(Object.assign({ host: h.label, hostKey: h.key }, s)));
      (h.failedTty || []).forEach((s) => rows.push(Object.assign({ host: h.label, hostKey: h.key }, s)));
    });
    rows.sort((a, b) => (a.start < b.start ? 1 : -1));
    return rows;
  }, [hosts]);

  // AI Assistant context — compact summary of what this tab shows (not raw data).
  const aiSummary = useMemo(() => {
    if (!data) return null;
    const L = [];
    L.push(`Linux Login Audit (tab: Login Audit) — period: last ${days} day(s), generated ${fmt(data.generatedAt)}, ${hosts.length} hosts (${totals.ok} reachable, ${totals.down} unreachable).`);
    L.push(`Totals: ${totals.success} successful SSH logins, ${totals.failed} failed, ${totals.ext} from external IPs, ${totals.root} root logins, ${totals.pwd} password logins, ${totals.active} active sessions, ${totals.dash || 0} from the dashboard itself.`);
    L.push('Per host:');
    hosts.forEach((h) => {
      if (!h.ok) { L.push(`- ${h.label}: UNREACHABLE${h.error ? ' (' + h.error + ')' : ''}`); return; }
      const s = h.summary || {};
      L.push(`- ${h.label}: success ${s.success}, failed ${s.failed}, external ok/fail ${s.externalSuccess}/${s.externalFailed}, root ${s.rootLogins}, password ${s.passwordLogins}, active ${s.activeSessions}`);
    });
    const ipAgg = {};
    events.forEach((e) => {
      if (e.fromDashboard) return;
      const k = e.ip || '?';
      const a = ipAgg[k] || (ipAgg[k] = { ip: k, ok: 0, fail: 0, users: new Set(), hosts: new Set(), external: !!e.external, last: e.time });
      if (e.result === 'success') a.ok += 1; else a.fail += 1;
      if (e.user) a.users.add(e.user);
      a.hosts.add(e.host);
    });
    const topIps = Object.values(ipAgg).sort((a, b) => (b.ok + b.fail) - (a.ok + a.fail)).slice(0, 15);
    if (topIps.length) {
      L.push('Top source IPs (sshd, excluding dashboard):');
      topIps.forEach((a) => L.push(`- ${a.ip}${a.external ? ' [external]' : ''}: ok ${a.ok}, failed ${a.fail}, users ${Array.from(a.users).slice(0, 6).join(',')}, hosts ${Array.from(a.hosts).join(',')}, last ${fmt(a.last)}`));
    }
    const recent = events.filter((e) => !e.fromDashboard).slice(0, 40);
    if (recent.length) {
      L.push('Latest 40 sshd events (time | host | result | user | method | ip):');
      recent.forEach((e) => L.push(`${fmt(e.time)} | ${e.host} | ${e.result} | ${e.user} | ${e.method || ''} | ${e.ip}${e.external ? ' [ext]' : ''}`));
    }
    hosts.filter((h) => h.ok && h.nginx).forEach((h) => {
      const n = h.nginx;
      L.push(`Nginx ${h.label}: ${n.total} requests, 4xx ${n.s4xx}, 5xx ${n.s5xx}, ${n.ips} client IPs (${n.externalIps} external), login ok ${n.loginOk}, login failed ${n.loginFail}, scanner probes ${n.probes} (${n.probesServed || 0} served real content).`);
      (n.topIps || []).slice(0, 10).forEach((r) => L.push(`  - ${r.ip}${r.external ? ' [external]' : ''}: ${r.total} req, 4xx ${r.s4xx}, 5xx ${r.s5xx}, loginFail ${r.loginFail}, probes ${r.probes}`));
    });
    return L.join('\n');
  }, [data, days, hosts, totals, events]);
  usePageAIContext('linux_login_audit', 'Linux Login Audit', aiSummary);

  const nginxSummary = hosts.filter((h) => h.ok && h.nginx)
    .map((h) => `${h.label}: ${h.nginx.total} req, ${h.nginx.s4xx + h.nginx.s5xx} err, ${h.nginx.probes} probes`).join(' · ');
  const ql = q.trim().toLowerCase();
  const filteredEvents = events.filter((e) =>
    (hostFilter === 'all' || e.hostKey === hostFilter) &&
    (resultFilter === 'all' || e.result === resultFilter) &&
    (!externalOnly || e.external) && (showDash || !e.fromDashboard) &&
    (!ql || `${e.user} ${e.ip} ${e.method} ${e.host}`.toLowerCase().includes(ql)));
  const filteredSessions = sessions.filter((s) =>
    (hostFilter === 'all' || s.hostKey === hostFilter) &&
    (resultFilter === 'all' || (resultFilter === 'success' ? s.kind === 'login' : s.kind === 'failed')) &&
    (!externalOnly || s.external) && (showDash || !s.fromDashboard) &&
    (!ql || `${s.user} ${s.from} ${s.tty} ${s.host}`.toLowerCase().includes(ql)));

  const exportCsv = () => {
    const isEv = tab === 'events';
    const head = isEv ? ['time', 'host', 'result', 'user', 'method', 'ip', 'port', 'external']
      : ['start', 'end', 'host', 'kind', 'user', 'tty', 'from', 'status', 'duration', 'external'];
    const rows = (isEv ? filteredEvents : filteredSessions).map((r) => head.map((k) => `"${String(r[k] == null ? '' : r[k]).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[head.join(',')].concat(rows).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `linux-login-audit-${tab}-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3" style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>Linux Login Audit</h1>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', margin: '3px 0 0' }}>SSH logins on every Linux EC2 host, read live from sshd logs. Read-only.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={days} onChange={(e) => setDays(+e.target.value)}
            className="rounded-lg bg-white/15 ring-1 ring-inset ring-white/30 text-white px-3 py-2 text-sm outline-none">
            {DAY_OPTIONS.map((d) => <option key={d} value={d} className="text-slate-800">Last {d} day{d > 1 ? 's' : ''}</option>)}
          </select>
          <button onClick={() => load(true)} disabled={loading}
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold disabled:opacity-60 shadow-sm"
            style={{ color: 'rgb(var(--brand-700))' }}>
            {loading ? 'Scanning hosts…' : '↻ Refresh'}
          </button>
          <button onClick={() => setAll(false)} className="rounded-lg bg-white/15 ring-1 ring-inset ring-white/30 px-3 py-2 text-sm text-white hover:bg-white/25">⊟ Collapse all</button>
          <button onClick={() => setAll(true)} className="rounded-lg bg-white/15 ring-1 ring-inset ring-white/30 px-3 py-2 text-sm text-white hover:bg-white/25">⊞ Expand all</button>
        </div>
      </div>

      {data && (
        <div className="text-xs text-gray-500">
          Generated {fmt(data.generatedAt)}{data.cached ? ' (cached, < 5 min old)' : ''} · {hosts.length} hosts
        </div>
      )}
      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && !data && <div className="rounded-lg border bg-white p-6 text-center text-sm text-gray-500">Connecting to all hosts over SSH — this can take up to 30 seconds…</div>}

      {data && (
        <>
          <Section id="summary" title="📊 Summary" summary={`${totals.success} successful · ${totals.failed} failed · ${totals.ext} external`} open={isOpen('summary')} onToggle={toggle}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            <Kpi label="Hosts reachable" value={`${totals.ok}/${hosts.length}`} tone={totals.down ? 'amber' : 'green'} sub={totals.down ? `${totals.down} unreachable` : 'all reachable'} />
            <Kpi label="Successful SSH" value={totals.success} tone="green" sub={`excl. ${totals.dash || 0} from dashboard`} />
            <Kpi label="Failed attempts" value={totals.failed} tone={totals.failed ? 'red' : 'neutral'} />
            <Kpi label="External IP events" value={totals.ext} tone={totals.ext ? 'red' : 'neutral'} sub="non-private source" />
            <Kpi label="Root logins" value={totals.root} tone={totals.root ? 'amber' : 'neutral'} />
            <Kpi label="Password logins" value={totals.pwd} tone={totals.pwd ? 'amber' : 'neutral'} sub="not key-based" />
            <Kpi label="Active sessions" value={totals.active} />
          </div>
          </Section>

          <Section id="hosts" title="🖥️ Hosts" summary={`${totals.ok}/${hosts.length} reachable${totals.down ? ` · ${totals.down} unreachable` : ''}`} open={isOpen('hosts')} onToggle={toggle}>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Host</th><th className="px-3 py-2">Hostname / OS</th>
                  <th className="px-3 py-2 text-right">Success</th><th className="px-3 py-2 text-right">Failed</th>
                  <th className="px-3 py-2 text-right">Users</th><th className="px-3 py-2 text-right">Source IPs</th>
                  <th className="px-3 py-2 text-right">External</th><th className="px-3 py-2 text-right">Root</th>
                  <th className="px-3 py-2 text-right">Active</th><th className="px-3 py-2">Last login</th>
                  <th className="px-3 py-2">Log source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {hosts.map((h) => (
                  <tr key={h.key} className={`cursor-pointer hover:bg-gray-50 ${hostFilter === h.key ? 'bg-teal-50' : ''}`}
                    onClick={() => setHostFilter(hostFilter === h.key ? 'all' : h.key)}>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${h.ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      {h.label}
                      <div className="text-[11px] text-gray-400">{h.user}@{h.ip}</div>
                    </td>
                    {h.ok ? (
                      <>
                        <td className="px-3 py-2"><div>{h.hostname}</div><div className="text-[11px] text-gray-400">{h.os}</div></td>
                        <td className="px-3 py-2 text-right text-emerald-700">{h.summary.success}</td>
                        <td className={`px-3 py-2 text-right ${h.summary.failed ? 'font-semibold text-red-600' : ''}`}>{h.summary.failed}</td>
                        <td className="px-3 py-2 text-right">{h.summary.uniqueUsers}</td>
                        <td className="px-3 py-2 text-right">{h.summary.uniqueIps}</td>
                        <td className={`px-3 py-2 text-right ${h.summary.externalSuccess + h.summary.externalFailed ? 'font-semibold text-red-600' : ''}`}>{h.summary.externalSuccess + h.summary.externalFailed}</td>
                        <td className={`px-3 py-2 text-right ${h.summary.rootLogins ? 'font-semibold text-amber-600' : ''}`}>{h.summary.rootLogins}</td>
                        <td className="px-3 py-2 text-right">{h.summary.activeSessions}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmt(h.summary.lastLogin)}</td>
                        <td className="px-3 py-2">
                          <Badge tone={h.logSource === 'journal' ? 'blue' : 'gray'}>{h.logSource || '—'}</Badge>
                          {!h.sudo && <span className="ml-1"><Badge tone="amber">no sudo</Badge></span>}
                        </td>
                      </>
                    ) : (
                      <td colSpan={10} className="px-3 py-2 text-red-600">Unreachable: {h.error}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </Section>

          <Section id="ssh" title="🔑 SSH events & sessions" summary={`${filteredEvents.length} events · ${filteredSessions.length} sessions`} open={isOpen('ssh')} onToggle={toggle}>
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3">
              {[['events', `SSH events (${filteredEvents.length})`], ['sessions', `Sessions last/lastb (${filteredSessions.length})`]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === k ? 'text-white' : 'bg-gray-100 text-gray-600'}`}
                  style={tab === k ? { backgroundColor: 'rgb(var(--brand-600))' } : undefined}>{l}</button>
              ))}
              <div className="mx-1 h-6 w-px bg-gray-200" />
              <select value={hostFilter} onChange={(e) => setHostFilter(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
                <option value="all">All hosts</option>
                {hosts.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
              </select>
              <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
                <option value="all">All results</option><option value="success">Success</option><option value="failed">Failed</option>
              </select>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input type="checkbox" checked={externalOnly} onChange={(e) => setExternalOnly(e.target.checked)} /> External IP only
              </label>
              <label className="flex items-center gap-1 text-sm text-gray-600" title="SSH logins whose source is the dashboard server itself: Remote Console sessions and this page's own scan">
                <input type="checkbox" checked={showDash} onChange={(e) => setShowDash(e.target.checked)} /> Show dashboard-originated
              </label>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user / IP…"
                className="min-w-[180px] flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
              <button onClick={exportCsv} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">⬇ CSV</button>
            </div>

            <div className="max-h-[65vh] overflow-auto">
              {tab === 'events' ? (
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr><th className="px-3 py-2">Time</th><th className="px-3 py-2">Host</th><th className="px-3 py-2">Result</th>
                      <th className="px-3 py-2">User</th><th className="px-3 py-2">Method</th><th className="px-3 py-2">Source IP</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEvents.slice(0, limit).map((e, i) => (
                      <tr key={i} className={e.result === 'failed' ? 'bg-red-50/40' : ''}>
                        <td className="px-3 py-1.5 whitespace-nowrap">{fmt(e.time)}</td>
                        <td className="px-3 py-1.5">{e.host}</td>
                        <td className="px-3 py-1.5"><Badge tone={e.result === 'success' ? 'green' : 'red'}>{e.result}</Badge></td>
                        <td className="px-3 py-1.5 font-mono">{e.user}{e.invalidUser && <span className="ml-1"><Badge tone="amber">invalid user</Badge></span>}{e.user === 'root' && <span className="ml-1"><Badge tone="amber">root</Badge></span>}</td>
                        <td className="px-3 py-1.5">{e.method}</td>
                        <td className="px-3 py-1.5 font-mono">{e.ip}{e.port ? `:${e.port}` : ''}{e.external && <span className="ml-1"><Badge tone="red">external</Badge></span>}{e.fromDashboard && <span className="ml-1"><Badge tone="blue">dashboard</Badge></span>}</td>
                      </tr>
                    ))}
                    {!filteredEvents.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No SSH events for this filter.</td></tr>}
                  </tbody>
                </table>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr><th className="px-3 py-2">Start</th><th className="px-3 py-2">End</th><th className="px-3 py-2">Host</th><th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">User</th><th className="px-3 py-2">TTY</th><th className="px-3 py-2">From</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Duration</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredSessions.slice(0, limit).map((s, i) => (
                      <tr key={i} className={s.kind === 'failed' ? 'bg-red-50/40' : ''}>
                        <td className="px-3 py-1.5 whitespace-nowrap">{fmt(s.start)}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{fmt(s.end)}</td>
                        <td className="px-3 py-1.5">{s.host}</td>
                        <td className="px-3 py-1.5"><Badge tone={s.kind === 'login' ? 'green' : 'red'}>{s.kind === 'login' ? 'login' : 'failed'}</Badge></td>
                        <td className="px-3 py-1.5 font-mono">{s.user}</td>
                        <td className="px-3 py-1.5">{s.tty}</td>
                        <td className="px-3 py-1.5 font-mono">{s.from || '—'}{s.external && <span className="ml-1"><Badge tone="red">external</Badge></span>}</td>
                        <td className="px-3 py-1.5"><Badge tone={s.status === 'active' ? 'blue' : 'gray'}>{s.status}</Badge></td>
                        <td className="px-3 py-1.5">{s.duration}</td>
                      </tr>
                    ))}
                    {!filteredSessions.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">No sessions for this filter.</td></tr>}
                  </tbody>
                </table>
              )}
            </div>
            {(tab === 'events' ? filteredEvents.length : filteredSessions.length) > limit && (
              <div className="border-t border-gray-100 p-3 text-center">
                <button onClick={() => setLimit(limit + PAGE)} className="text-sm font-medium" style={{ color: 'rgb(var(--brand-700))' }}>Show more</button>
              </div>
            )}
          </div>
          </Section>

          {hosts.some((h) => h.ok && h.nginx) && (
            <Section id="nginx" title="🌐 Nginx access" summary={nginxSummary} open={isOpen('nginx')} onToggle={toggle}>
              <NginxPanel hosts={hosts} />
            </Section>
          )}
        </>
      )}
    </div>
  );
}

const TOP_TABS = [['login', '🔑 Login Audit'], ['review', '🛡️ Security Review']];
export default function LinuxLoginAudit() {
  const [top, setTop] = useState(() => { try { return localStorage.getItem('linuxLoginAudit.topTab') || 'login'; } catch (_) { return 'login'; } });
  const pick = (k) => { setTop(k); try { localStorage.setItem('linuxLoginAudit.topTab', k); } catch (_) {} };
  return (
    <div>
      <div className="flex gap-1 border-b border-gray-200 px-4 pt-4 md:px-6">
        {TOP_TABS.map(([k, l]) => (
          <button key={k} onClick={() => pick(k)}
            className={`-mb-px rounded-t-lg border px-4 py-2 text-sm font-semibold ${top === k ? 'border-gray-200 border-b-white bg-white text-gray-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>
      {top === 'login' ? <LoginAuditView /> : <div className="p-4 md:p-6"><SecurityReviewPanel /></div>}
    </div>
  );
}
